import React from 'react';

/**
 * ProvisionMart brand logo — pure inline SVG (no raster asset, no font deps).
 *
 * Spec compliance:
 *   • Letters "P" + "M" integrated into a shopping-cart icon
 *   • Minimal, modern, flat — zero gradients
 *   • Primary  #16A34A (green)   — cart body
 *   • Accent   #F97316 (orange)  — handle + wheels accent
 *   • Inter / system sans-serif typography
 *   • Scales to any size — viewBox 0 0 64 64 for the mark
 *
 * Variants:
 *   - "mark"        → cart + PM only (square — favicon, app icon, tight nav)
 *   - "horizontal"  → mark + wordmark side-by-side (default)
 *   - "stacked"     → mark on top, wordmark below (auth pages / hero)
 *
 * Theming: respects CSS custom properties (`--pm-color-primary`,
 * `--pm-color-accent`, `--pm-color-text-muted`) so it auto-flips in dark mode.
 */

type Variant = 'mark' | 'horizontal' | 'stacked';

interface LogoProps {
  size?: number;
  variant?: Variant;
  invert?: boolean;
  className?: string;
  style?: React.CSSProperties;
  tagline?: string | boolean;
  showTagline?: boolean;
  showWordmark?: boolean;
  /** Forces a darker palette (white-on-dark backgrounds). */
  dark?: boolean;
  /** Override the icon's pixel size; defaults to `size`. */
  iconSize?: number;
  /** Extra crop multiplier on the icon artwork (1 = default tighten). */
  iconCropScale?: number;
}

export const DEFAULT_TAGLINE = 'Fresh groceries from your neighbourhood — delivered fast';
export const BRAND_PRIMARY = '#16A34A';
export const BRAND_ACCENT  = '#F97316';
export const BRAND_DARK    = '#0F172A';

interface PMMarkProps {
  size: number;
  primary?: string;
  accent?: string;
  monogram?: string;
  /**
   * When true (default), the source PNG is visually cropped via a CSS scale
   * so the cart artwork fills the requested `size` box. The asset itself
   * carries ~15–20% transparent padding around the cart, which makes the
   * mark look comically small at typical UI sizes — this fixes that without
   * re-exporting the asset.
   */
  tighten?: boolean;
  /** Extra crop multiplier applied on top of `tighten`. 1 = default. */
  cropScale?: number;
}

/** Public asset path for the ProvisionMart icon (square PNG). */
export const LOGO_ICON_SRC = '/pmlogo.png';

export const PMMark: React.FC<PMMarkProps> = ({ size, tighten = true, cropScale = 1 }) => {
  const baseScale = tighten ? 1.22 : 1;
  const scale = baseScale * cropScale;
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        overflow: 'hidden',
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <img
        src={LOGO_ICON_SRC}
        alt="ProvisionMart"
        width={size}
        height={size}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: 'transparent',
          transform: scale !== 1 ? `scale(${scale})` : 'none',
          transformOrigin: 'center center',
        }}
        draggable={false}
      />
    </span>
  );
};

const Wordmark: React.FC<{ size: number; invert?: boolean; dark?: boolean }> = ({ size, invert, dark }) => (
  <span
    style={{
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      fontWeight: 800,
      // Keep the wordmark slightly smaller than the mark so the lockup feels
      // balanced at every size (navbar 56 → ~26px, auth 104 → ~47px).
      fontSize: Math.round(size * 0.46),
      letterSpacing: '-0.01em',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }}
  >
    <span style={{ color: invert ? '#FFFFFF' : (dark ? BRAND_DARK : `var(--pm-color-primary, ${BRAND_PRIMARY})`) }}>
      Provision
    </span>
    <span style={{ color: `var(--pm-color-accent, ${BRAND_ACCENT})` }}>Mart</span>
  </span>
);

const Logo: React.FC<LogoProps> = ({
  size = 56,
  variant = 'horizontal',
  invert = false,
  className,
  style,
  tagline,
  showTagline,
  showWordmark,
  dark = false,
  iconSize,
  iconCropScale = 1,
}) => {
  const showCaption =
    tagline === true || showTagline === true || (typeof tagline === 'string' && tagline.length > 0);
  const captionText = typeof tagline === 'string' && tagline ? tagline : DEFAULT_TAGLINE;

  const wordmarkVisible = showWordmark ?? variant !== 'mark';
  const stacked = variant === 'stacked';
  const resolvedIconSize = iconSize ?? size;

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: stacked ? 6 : 4,
        ...style,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: 'center',
          gap: stacked ? 0 : Math.max(8, Math.round(size * 0.22)),
        }}
      >
        <PMMark size={resolvedIconSize} cropScale={iconCropScale} />
        {wordmarkVisible && (
          <span
            style={
              stacked
                ? {
                    // Pull the wordmark up to close the visual gap left by:
                    //  (a) any residual transparent padding at the bottom of the
                    //      icon PNG even after `tighten` + `cropScale`, and
                    //  (b) Inter's intrinsic leading above the caps.
                    marginTop: -Math.round(resolvedIconSize * 0.32),
                    display: 'inline-block',
                  }
                : undefined
            }
          >
            <Wordmark size={size} invert={invert} dark={dark} />
          </span>
        )}
      </div>
      {showCaption && (
        <span
          style={{
            // Slightly smaller so it visually subordinates to the wordmark.
            fontSize: Math.max(10, Math.round(size * 0.15)),
            fontWeight: 500,
            letterSpacing: 0.2,
            color: invert ? 'rgba(255,255,255,0.78)' : 'var(--pm-color-text-muted, #6B7280)',
            textAlign: 'center',
            lineHeight: 1.25,
            // Constrain to roughly the visual width of "ProvisionMart" so the
            // caption never extends beyond the wordmark — it will wrap instead.
            // Wordmark fontSize ≈ size × 0.46 and "ProvisionMart" is ~7em wide
            // in Inter Bold, so size × 3.2 lines up under the wordmark.
            maxWidth: Math.round(size * 3.2),
            wordBreak: 'break-word',
          }}
        >
          {captionText}
        </span>
      )}
    </div>
  );
};

export default Logo;

/* ---- Static SVG strings for non-React use (favicon, app icon) -----------
 * The PNG icon at `/pmlogo.png` is the canonical mark. These wrappers embed
 * it inside an <svg> so callers expecting an SVG string still work.
 */

const _markSvg = (): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" width="512" height="512">` +
  `<image href="${LOGO_ICON_SRC}" xlink:href="${LOGO_ICON_SRC}" width="512" height="512" preserveAspectRatio="xMidYMid meet"/>` +
  `</svg>`;

export const LOGO_SVG_LIGHT = _markSvg();
export const LOGO_SVG_DARK  = _markSvg();

